import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { tip } from '../../ui/tooltip';
import { dragDelay, dragEdge, dragLength, resizeMotion, rulerMarks, rulerSpan, stripRows, xToTime, type MotionPhase, type MotionResize, type Span, type StripInput, type StripRow } from './timingStripModel';
import type { Phase } from './animateModel';

/**
 * The timeline: every layer of the page on one ruler, for putting them one after another.
 * The part of the Animate bar that Timing opens — out only for somebody who asked for it.
 *
 * A row per direct child of the page, top of the stack first as the Layers panel reads.
 * Each bar is when that layer is on the page; its arriving and leaving are tinted at the
 * ends and a loop is hatched between. These controls can be dragged, each one undo step,
 * written when the hand lets go:
 *
 *   an end of a bar      when the layer appears or goes
 *   a purple end         the duration of its Enter or Exit animation
 *   a layer's arriving   when, inside its bar, it comes in
 *   the page's end       how long the page lasts (the ruler shows room past it)
 *   the ruler            the playhead — the page is held on that instant, and it stays
 *                        there: through a pause, a click on the page, a move tried
 *
 * When each layer arrives, loops and leaves is the engine's `stageSchedule`, never worked
 * out again here. The playhead follows the canvas's own clock, read each frame.
 */

const ROW = 30;
/** Rows shown before the list scrolls. */
const MOST_ROWS = 6;

/** What is being dragged. `at` is the instant under the hand, said on the ruler while it moves. */
type Drag =
  | { kind: 'window'; id: string; window: Span; at: number }
  | { kind: 'arrive'; id: string; delay: number; at: number }
  | { kind: 'motion'; id: string; phase: MotionPhase; resize: MotionResize; at: number }
  | { kind: 'length'; seconds: number; span: number; at: number };

export function TimingStrip({
  data,
  selected,
  playhead,
  onSelect,
  onPhase,
  onResizeMotion,
  onTiming,
  onDelay,
  onLength,
  onSeek,
}: {
  /** `length` is how long the page PLAYS, which can be longer than it is set to last. */
  data: { length: number; children: readonly StripInput[] };
  selected: readonly string[];
  /** Where the playhead is. `resting`: nothing is playing or held — it is only where playing starts from. */
  playhead: () => { seconds: number; resting?: boolean } | null;
  onSelect: (id: string) => void;
  onPhase: (id: string, phase: Phase) => void;
  onResizeMotion: (id: string, phase: MotionPhase, resize: MotionResize) => void;
  onTiming: (id: string, window: Span) => void;
  /** A layer's wait, inside its window, before it arrives. */
  onDelay: (id: string, delay: number) => void;
  onLength: (seconds: number) => void;
  onSeek: (seconds: number) => void;
}) {
  const rows = stripRows(data);
  const length = data.length;
  const [drag, setDrag] = useState<Drag | null>(null);
  const cleanGesture = useRef<(() => void) | null>(null);
  useEffect(() => () => cleanGesture.current?.(), []);
  // Held still while the page's end is dragged: a ruler that rescales under the hand
  // moves the very thing being dragged.
  const span = drag?.kind === 'length' ? drag.span : rulerSpan(length);

  const ruler = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ruler.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    setWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  // The playhead: moved straight on the element each frame, never through a render. It is
  // always there — at the start until something is played, then wherever it was left —
  // and fainter while the page is not being shown on that instant.
  const head = useRef<HTMLDivElement>(null);
  const clock = useRef(playhead);
  clock.current = playhead;
  const shown = useRef(span);
  shown.current = span;
  useEffect(() => {
    let frame = 0;
    const tick = () => {
      const el = head.current;
      const at = clock.current();
      if (el) {
        el.style.opacity = at && !at.resting ? '1' : '0.45';
        el.style.left = `${(Math.min(shown.current, at?.seconds ?? 0) / shown.current) * 100}%`;
      }
      frame = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(frame);
  }, []);

  const pct = (seconds: number) => `${(Math.max(0, Math.min(span, seconds)) / span) * 100}%`;
  const share = (seconds: number) => `${(Math.max(0, seconds) / span) * 100}%`;

  /** One gesture on a handle: `move` for every change of the pointer, `done` when it lets go. */
  const gesture = (event: ReactPointerEvent<HTMLElement>, move: (x: number, track: DOMRect) => void, done: () => void) => {
    if (event.button !== 0) return;
    const track = ruler.current?.getBoundingClientRect();
    if (!track) return;
    event.preventDefault();
    event.stopPropagation();
    const handle = event.currentTarget;
    const pointer = event.pointerId;
    cleanGesture.current?.();
    handle.setPointerCapture(pointer);
    const onMove = (e: PointerEvent) => { if (e.pointerId === pointer) move(e.clientX, track); };
    const cleanup = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      try {
        handle.releasePointerCapture(pointer);
      } catch {
        // Already released: the gesture still ends.
      }
      cleanGesture.current = null;
    };
    const up = (e: PointerEvent) => { if (e.pointerId !== pointer) return; cleanup(); setDrag(null); done(); window.dispatchEvent(new Event('artwork:hide-tip')); };
    const cancel = (e: PointerEvent) => { if (e.pointerId !== pointer) return; cleanup(); setDrag(null); window.dispatchEvent(new Event('artwork:hide-tip')); };
    cleanGesture.current = cleanup;
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    move(event.clientX, track);
  };

  const grabEdge = (row: StripRow, edge: 'from' | 'until') => (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    if (!selected.includes(row.id)) onSelect(row.id);
    const start = row.window;
    const startX = event.clientX;
    let last = start;
    gesture(
      event,
      (x, track) => {
        if (Math.abs(x - startX) < 2 && last === start) return;
        last = dragEdge(start, edge, start[edge] + (x - startX) / track.width * span, length);
        setDrag({ kind: 'window', id: row.id, window: last, at: last[edge] });
      },
      () => {
        if (last.from !== start.from || last.until !== start.until) onTiming(row.id, last);
      },
    );
  };

  const grabArriving = (row: StripRow) => (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    const enter = row.enter;
    if (!enter) return;
    onPhase(row.id, 'in');
    const was = dragDelay({ window: row.window, enter }, 0);
    const from = event.clientX;
    let last = was;
    gesture(
      event,
      (x, track) => {
        last = dragDelay({ window: row.window, enter }, ((x - from) / Math.max(1, track.width)) * span);
        setDrag({ kind: 'arrive', id: row.id, delay: last, at: row.window.from + last });
      },
      () => {
        if (last !== was) onDelay(row.id, last);
      },
    );
  };

  const grabMotionEdge = (row: StripRow, phase: MotionPhase, edge: 'from' | 'until') => (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    const original = phase === 'in' ? row.enter : row.exit;
    if (!original) return;
    onPhase(row.id, phase);
    const startX = event.clientX;
    let last: MotionResize | null = null;
    gesture(event, (x, track) => {
      if (Math.abs(x - startX) < 2 && !last) return;
      last = resizeMotion(row, phase, edge, original[edge] + (x - startX) / track.width * span, length);
      if (last) setDrag({ kind: 'motion', id: row.id, phase, resize: last, at: last.range[edge] });
    }, () => {
      if (last && (Math.abs(last.range.from - original.from) > 0.001 || Math.abs(last.range.until - original.until) > 0.001)) onResizeMotion(row.id, phase, last);
    });
  };

  const grabEnd = (event: ReactPointerEvent<HTMLElement>) => {
    const held = span;
    const startX = event.clientX;
    let last = length;
    gesture(
      event,
      (x, track) => {
        if (Math.abs(x - startX) < 2 && last === length) return;
        last = dragLength(length + (x - startX) / track.width * held);
        setDrag({ kind: 'length', seconds: last, span: held, at: last });
      },
      () => {
        if (last !== length) onLength(last);
      },
    );
  };

  const scrub = (event: ReactPointerEvent<HTMLElement>) => {
    gesture(
      event,
      (x, track) => onSeek(Math.min(length, xToTime(x - track.left, span, track.width))),
      () => {},
    );
  };

  const marks = rulerMarks(span, width);
  const end = drag?.kind === 'length' ? drag.seconds : length;

  return (
    <div aria-label="Timeline" role="group" data-dragging={drag?.kind} className="timing-rows relative mt-[6px] select-none">
      <div className="flex h-[26px] items-center">
        <span className="shrink-0" style={{ width: 'var(--timing-name)' }} />
        <div ref={ruler} onPointerDown={scrub} className="relative mr-[14px] h-full min-w-0 flex-1 cursor-ew-resize touch-none">
          {marks.map((mark) => (
            <span key={mark} className={`tnum pointer-events-none absolute top-[7px] -translate-x-1/2 text-[10px] first:translate-x-0 ${mark > end + 1e-6 ? 'text-label-4' : 'text-label-3'}`} style={{ left: pct(mark) }}>
              {mark === 0 ? '0' : `${mark}s`}
            </span>
          ))}
          {/* The instant under the hand, while something is dragged: a bar's end gives no other sign of where it is. */}
          {drag ? (
            <span className="tnum pointer-events-none absolute top-[3px] z-[4] -translate-x-1/2 whitespace-nowrap rounded-full bg-label px-[7px] text-[11px] font-medium leading-[18px] text-canvas" style={{ left: pct(drag.at) }}>
              {drag.kind === 'motion' ? `${round(drag.resize.range.until - drag.resize.range.from)} s duration` : `${round(drag.at)} s`}
            </span>
          ) : null}
        </div>
      </div>
      <div className="ui-scroll relative flex min-h-0 flex-col" style={{ maxHeight: MOST_ROWS * ROW }}>
        {rows.map((row) => {
          const resizing = drag?.kind === 'motion' && drag.id === row.id ? drag : null;
          const window = drag?.kind === 'window' && drag.id === row.id ? drag.window : resizing?.resize.window ?? row.window;
          const chosen = selected.includes(row.id);
          const dragging = drag && 'id' in drag && drag.id === row.id;
          const bar = Math.max(0.001, window.until - window.from);
          // Arriving and leaving keep their length while a drag moves the window's ends.
          const enter = resizing?.phase === 'in' ? resizing.resize.range.until - resizing.resize.range.from : row.enter ? Math.min(bar, row.enter.until - row.enter.from) : 0;
          const enterAt = resizing?.phase === 'in' ? resizing.resize.range.from - window.from : drag?.kind === 'arrive' && drag.id === row.id ? drag.delay : row.enter ? Math.max(0, row.enter.from - row.window.from) : 0;
          const exit = resizing?.phase === 'out' ? resizing.resize.range.until - resizing.resize.range.from : row.exit ? Math.min(bar, row.exit.until - row.exit.from) : 0;
          const motionPart = (phase: MotionPhase, from: number, duration: number) => (
            <span role="button" tabIndex={0} aria-label={`${row.name} ${phase === 'in' ? 'Enter' : 'Exit'} animation`}
              {...tip(`${phase === 'in' ? 'Enter' : 'Exit'} animation · Drag edges to resize`)}
              onPointerDown={phase === 'in' ? grabArriving(row) : (event) => { event.stopPropagation(); onPhase(row.id, phase); }}
              onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); onPhase(row.id, phase); } }}
              className={`strip-motion absolute inset-y-0 rounded-[6px] bg-accent ${phase === 'in' ? 'cursor-grab active:cursor-grabbing' : 'strip-exit cursor-pointer'}`}
              style={{ left: `${(from / bar) * 100}%`, width: `${(duration / bar) * 100}%` }}>
              {row.animation ? (['from', 'until'] as const).map(edge => (
                <span key={edge} role="slider" tabIndex={0} aria-label={`${row.name} ${phase === 'in' ? 'Enter' : 'Exit'} ${edge === 'from' ? 'start' : 'end'}`}
                  aria-valuemin={window.from} aria-valuemax={length} aria-valuenow={round(window.from + from + (edge === 'until' ? duration : 0))}
                  onPointerDown={grabMotionEdge(row, phase, edge)}
                  onKeyDown={(event) => {
                    if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
                    event.preventDefault(); event.stopPropagation();
                    const at = window.from + from + (edge === 'until' ? duration : 0);
                    const next = resizeMotion(row, phase, edge, at + (event.key === 'ArrowRight' ? 1 : -1) * (event.shiftKey ? 0.5 : 0.05), length);
                    if (next) { onPhase(row.id, phase); onResizeMotion(row.id, phase, next); }
                  }}
                  className={`strip-motion-grip ${edge === 'from' ? 'strip-motion-start' : 'strip-motion-end'}`}><span /></span>
              )) : null}
            </span>
          );
          return (
            <div key={row.id} data-selected={chosen || undefined} className={`strip-row flex shrink-0 items-center rounded-[8px] ${chosen ? 'bg-accent-soft' : ''}`} style={{ height: ROW }}>
              <button
                type="button"
                onClick={() => onSelect(row.id)}
                className={`h-full shrink-0 truncate pl-[10px] pr-[8px] text-left text-[12px] ${chosen ? 'font-medium text-label' : 'text-label-2 hover:text-label'}`}
                style={{ width: 'var(--timing-name)' }}
              >
                {row.name}
              </button>
              <div className="relative mr-[14px] h-full min-w-0 flex-1" onPointerDown={() => onSelect(row.id)}>
                <div
                  className={`absolute top-[7px] h-[16px] overflow-hidden rounded-[6px] ${row.timed || row.own ? 'bg-fill-selected' : 'bg-fill-1'}`}
                  style={{ left: pct(window.from), width: share(bar) }}
                >
                  {row.loops ? <button type="button" aria-label={`${row.name} Loop animation`} onPointerDown={event => event.stopPropagation()} onClick={() => onPhase(row.id, 'loop')} className="strip-loop absolute inset-y-[3px]" style={{ left: `${((enterAt + enter) / bar) * 100}%`, right: 0 }} /> : null}
                  {row.exit ? motionPart('out', bar - exit, exit) : null}
                  {row.enter ? motionPart('in', enterAt, enter) : null}
                </div>
                {/* The ends: wide enough for a finger, drawn as a grip only where it helps. */}
                {(['from', 'until'] as const).map((edge) => (
                  <span
                    key={edge}
                    role="slider"
                    aria-label={edge === 'from' ? `${row.name} appears` : `${row.name} goes`}
                    aria-valuemin={0}
                    aria-valuemax={length}
                    aria-valuenow={window[edge]}
                    {...tip(`${edge === 'from' ? 'Appears' : 'Goes'} at ${round(window[edge])} s`)}
                    onPointerDown={grabEdge(row, edge)}
                    className={`touch-ring group absolute top-[4px] z-[1] flex h-[22px] w-[12px] cursor-ew-resize touch-none items-center ${edge === 'from' ? '-translate-x-full justify-end' : 'justify-start'}`}
                    style={{ left: pct(window[edge]) }}
                  >
                    <span className={`h-[14px] w-[3px] rounded-full bg-label transition-opacity ${chosen || dragging ? 'opacity-80' : 'opacity-0 group-hover:opacity-80 pointer-coarse:opacity-80'}`} />
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {/* Over the ruler and the rows together: what is past the page's end, the end itself, the playhead. */}
      <div className="pointer-events-none absolute inset-y-0 right-[14px]" style={{ left: 'var(--timing-name)' }}>
        <div aria-hidden className="strip-past absolute inset-y-0 right-0 rounded-[4px]" style={{ left: pct(end) }} />
        <span
          role="slider"
          aria-label="Artboard ends"
          aria-valuemin={0.5}
          aria-valuemax={60}
          aria-valuenow={end}
          {...tip(`The artboard ends at ${round(end)} s`)}
          onPointerDown={grabEnd}
          className="pointer-events-auto absolute top-0 z-[2] flex h-[26px] w-[14px] -translate-x-1/2 cursor-ew-resize touch-none justify-center"
          style={{ left: pct(end) }}
        >
          <span className="mt-[2px] h-[12px] w-[8px] rounded-[2px] bg-danger" />
        </span>
        <span aria-hidden className="absolute inset-y-0 w-px bg-danger/60" style={{ left: pct(end) }} />
        <div ref={head} aria-hidden className="strip-head absolute inset-y-0 z-[3] w-[2px] -translate-x-1/2 rounded-full bg-label opacity-0" />
      </div>
    </div>
  );
}

const round = (value: number) => Math.round(value * 100) / 100;
